import {
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  forwardRef,
} from '@angular/core';
import {
  FormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { FloatLabelModule } from 'primeng/floatlabel';
import { AsyncPipe } from '@angular/common';
import { MaterialsService } from '../../materials.service';
import { map } from 'rxjs/operators';
import { Material } from '../../../models/material.model';
import {
  INHERIT_MATERIAL_ID,
  INHERIT_MATERIAL_LABEL,
} from '../../../models/quote-item-data.model';

@Component({
  selector: 'app-material-select',
  imports: [FormsModule, SelectModule, FloatLabelModule, AsyncPipe],
  templateUrl: './material-select.component.html',
  styleUrl: './material-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MaterialSelectComponent),
      multi: true,
    },
  ],
})
export class MaterialSelectComponent implements ControlValueAccessor {
  // TODO: Angular V21 upgrade: Remove ControlValueAccessor API and use FormControl instead (or whatever the new API is)
  private materialsService = inject(MaterialsService);

  @Input() placeholder = 'Select a material';
  @Input() label: string | null = null;
  private _disabledByForm = false; // Disabled state from reactive form (setDisabledState)
  private _disabledByInput = false; // Disabled state from @Input binding

  @Input()
  get disabled(): boolean {
    return this._disabledByInput;
  }
  set disabled(value: boolean) {
    this._disabledByInput = value;
  }

  // Computed property that combines both disabled states
  get isDisabled(): boolean {
    return this._disabledByForm || this._disabledByInput;
  }

  private _setNone = false;
  @Input()
  set setNone(value: boolean) {
    this._setNone = value;
    if (value) {
      this.value = null;
      this.valueChange.emit(null);
      this.onChange(null);
    }
  }
  get setNone(): boolean {
    return this._setNone;
  }
  @Input() value: number | null = null;
  @Output() valueChange = new EventEmitter<number | null>();
  @Input() includeInheritOption = true;

  // ControlValueAccessor implementation
  private onChange = (_: number | null) => {
    void _; // Reference parameter to satisfy linter
  };
  private onTouched = () => {};

  // Map active materials to options format for the dropdown
  materialOptions$ = this.materialsService.activeMaterials$.pipe(
    map((materials: Material[]) => {
      const options: Array<{ label: string; value: number | null }> = [
        { label: 'None', value: null },
      ];

      if (this.includeInheritOption) {
        options.push({
          label: INHERIT_MATERIAL_LABEL,
          value: INHERIT_MATERIAL_ID,
        });
      }

      options.push(
        ...materials.map((material) => ({
          label: material.name,
          value: material.id,
        })),
      );

      return options;
    }),
  );

  onMaterialChange(materialId: number | null): void {
    this.value = materialId;
    this.valueChange.emit(materialId);
    this.onChange(materialId);
    this.onTouched();
  }

  // ControlValueAccessor methods
  writeValue(value: number | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByForm = isDisabled;
  }
}
